import type { QueryKey } from '@tanstack/vue-query'

import type { WorkspaceIdentifier } from '../types'

import { useQueryClientUtils } from '../useQueryClientUtils'
import { useMutationDocumentTag } from './tag/useMutationDocumentTag'
import { useMutationDocumentType } from './type/useMutationDocumentType'
import { useMutationDocumentParty } from './party/useMutationDocumentParty'
import { useMutationDocumentReminder } from './reminder/useMutationDocumentReminder'
import { useMutationDocumentLanguage } from './language/useMutationDocumentLanguage'
import { defaultDocumentsFieldsToInclude, useFetchDocument } from './useFetchDocument'
import { useMutationDocumentRetention } from './retention/useMutationDocumentRetention'
import { useMutationDocumentTermsAndConditions } from './terms-and-conditions/useMutationDocumentTermsAndConditions'
import {
  queryKeys as documentQueryKeys,
  type QueryDocumentAllCacheData,
  type QueryDocumentAllInfiniteCacheData,
  type QueryDocumentByContractIdCacheData,
  type QueryDocumentByIdCacheData,
} from './useQueryDocument'

const sendViaEmail = () => {
  const { sendViaEmail } = useFetchDocument()

  return useMutation({
    mutationFn: sendViaEmail,
  })
}

const duplicate = () => {
  const { duplicate } = useFetchDocument()
  const { cancelQueries, invalidateQueries } = useQueryClientUtils()

  return useMutation({
    mutationFn: duplicate,
    onMutate: () => {
      cancelQueries([
        documentQueryKeys.all._def,
        documentQueryKeys.allInfinite._def,
      ])
    },
    onSettled: () => {
      invalidateQueries([
        documentQueryKeys.all._def,
        documentQueryKeys.allInfinite._def,
      ])
    },
  })
}

const upload = ({ omitOnSettledInvalidation }: { omitOnSettledInvalidation?: boolean } = {}) => {
  const { upload: uploadRequest } = useFetchDocument()
  const { invalidateQueries, cancelQueries } = useQueryClientUtils()

  return useMutation({
    mutationFn: ({ workspaceId, payload }: WorkspaceIdentifier & { payload: { folder_id: string, file: File } }) => {
      const data = new FormData()
      data.append('folder_id', payload.folder_id)
      data.append(`documents[0][file]`, payload.file)
      data.append(`documents[0][title]`, payload.file.name)

      return uploadRequest({
        workspaceId,
        payload: data,
        fieldsToInclude: defaultDocumentsFieldsToInclude,
      })
    },
    onMutate: () => {
      cancelQueries([
        documentQueryKeys.all._def,
        documentQueryKeys.allInfinite._def,
      ])
    },
    onSettled: () => {
      if (omitOnSettledInvalidation)
        return

      invalidateQueries([
        documentQueryKeys.all._def,
        documentQueryKeys.allInfinite._def,
      ])
    },
  })
}

const update = () => {
  const { update } = useFetchDocument()
  const queryClient = useQueryClient()
  const { cancelQueries, invalidateQueries } = useQueryClientUtils()

  return useMutation({
    mutationFn: (args: Omit<Parameters<typeof update>[0], 'fieldsToInclude'>) => update({
      ...args,
      fieldsToInclude: defaultDocumentsFieldsToInclude,
    }),
    onMutate: (variables) => {
      cancelQueries([
        documentQueryKeys.byId({
          id: variables.id,
          workspaceId: variables.workspaceId,
        }).queryKey,
        documentQueryKeys.byContractId._def,
        documentQueryKeys.all._def,
        documentQueryKeys.allInfinite._def,
      ])
    },
    onError: (_, variables) => {
      invalidateQueries([
        documentQueryKeys.byId({
          id: variables.id,
          workspaceId: variables.workspaceId,
        }).queryKey,
        documentQueryKeys.byContractId._def,
        documentQueryKeys.all._def,
        documentQueryKeys.allInfinite._def,
      ])
    },
    onSettled: (_, _1, variables) => {
      queryClient.invalidateQueries({
        queryKey: documentQueryKeys.byId({ id: variables.id, workspaceId: variables.workspaceId }).queryKey,
        refetchType: 'none',
      })
      queryClient.invalidateQueries({
        queryKey: documentQueryKeys.byContractId._def,
        refetchType: 'none',
      })

      invalidateQueries([
        documentQueryKeys.all._def,
        documentQueryKeys.allInfinite._def,
      ])
    },
    onSuccess: (responseData, variables) => {
      queryClient.setQueriesData<QueryDocumentByIdCacheData>({
        queryKey: documentQueryKeys.byId({
          id: variables.id,
          workspaceId: variables.workspaceId,
        }).queryKey,
      }, (oldData) => {
        if (!oldData)
          return oldData

        return {
          ...oldData,
          data: {
            ...oldData.data,
            ...responseData.data,
          },
        }
      }, { updatedAt: Date.now() })

      queryClient.setQueriesData<QueryDocumentByContractIdCacheData>({
        queryKey: documentQueryKeys.byContractId._def,
      }, (oldData) => {
        if (!oldData || oldData.id !== variables.id)
          return oldData

        return {
          ...oldData,
          ...responseData.data,
        }
      }, { updatedAt: Date.now() })
    },
  })
}

interface DropMutationContext {
  previousAll: Array<[QueryKey, QueryDocumentAllCacheData | undefined]>
  previousAllInfinite: Array<[QueryKey, QueryDocumentAllInfiniteCacheData | undefined]>
}

const drop = () => {
  const { drop: mutationFn } = useFetchDocument()
  const { invalidateQueries } = useQueryClientUtils()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn,
    onMutate: async (variables): Promise<DropMutationContext> => {
      const byIdQueryKey = documentQueryKeys.byId({
        id: variables.id,
        workspaceId: variables.workspaceId,
      }).queryKey

      await Promise.all([
        queryClient.cancelQueries({ queryKey: byIdQueryKey }),
        queryClient.cancelQueries({ queryKey: documentQueryKeys.all._def }),
        queryClient.cancelQueries({ queryKey: documentQueryKeys.allInfinite._def }),
      ])

      const previousAll = queryClient.getQueriesData<QueryDocumentAllCacheData>({
        queryKey: documentQueryKeys.all._def,
      })
      const previousAllInfinite = queryClient.getQueriesData<QueryDocumentAllInfiniteCacheData>({
        queryKey: documentQueryKeys.allInfinite._def,
      })

      queryClient.setQueriesData<QueryDocumentAllInfiniteCacheData>({
        queryKey: documentQueryKeys.allInfinite._def,
      }, (oldData) => {
        if (!oldData)
          return oldData

        return {
          ...oldData,
          pages: oldData.pages.map(page => ({
            ...page,
            data: page.data.filter(item => item.id !== variables.id),
          })),
        }
      })

      queryClient.setQueriesData<QueryDocumentAllCacheData>({
        queryKey: documentQueryKeys.all._def,
      }, (oldData) => {
        if (!oldData)
          return oldData

        return {
          ...oldData,
          data: oldData.data.filter(item => item.id !== variables.id),
        }
      })

      return { previousAll, previousAllInfinite }
    },
    onError: (_error, _variables, context) => {
      context?.previousAll.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
      context?.previousAllInfinite.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
    },
    onSettled: (_, _1, variables) => {
      queryClient.invalidateQueries({
        queryKey: documentQueryKeys.byId({
          id: variables.id,
          workspaceId: variables.workspaceId,
        }).queryKey,
      })
      invalidateQueries([
        documentQueryKeys.all._def,
        documentQueryKeys.allInfinite._def,
      ])
    },
    onSuccess: (_, variables) => {
      queryClient.removeQueries({
        queryKey: documentQueryKeys.byId({
          id: variables.id,
          workspaceId: variables.workspaceId,
        }).queryKey,
      })
    },
  })
}

export const useMutationDocument = {
  duplicate,
  sendViaEmail,
  update,
  drop,
  upload,

  tag: useMutationDocumentTag,
  type: useMutationDocumentType,
  party: useMutationDocumentParty,
  reminder: useMutationDocumentReminder,
  language: useMutationDocumentLanguage,
  retention: useMutationDocumentRetention,
  termsAndConditions: useMutationDocumentTermsAndConditions,
}
